import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "../../../utils/dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faXmark } from "@fortawesome/free-solid-svg-icons";

import { getUnreadCount, getNotifications } from "../../../api/system/notifications";
import { hasPermission } from "../../../utils/permissionUtils";
import { DESKTOP_UNREAD_REMINDER_KEY } from "../../../constants/desktopUnreadReminder";

const DISPLAY_MS = 5000;
const PREVIEW_LIMIT = 5;

export default function DesktopUnreadReminder() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [preview, setPreview] = useState([]);
  const timerRef = useRef(null);

  const markDone = () => {
    try {
      sessionStorage.setItem(DESKTOP_UNREAD_REMINDER_KEY, "1");
    } catch (_) {
      /* ignore */
    }
  };

  const close = () => {
    setVisible(false);
    markDone();
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        if (sessionStorage.getItem(DESKTOP_UNREAD_REMINDER_KEY)) return;

        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }

        const unreadRes = await getUnreadCount();
        const count = typeof unreadRes === "number" ? unreadRes : unreadRes?.count ?? 0;
        const mandatoryUnread =
          typeof unreadRes === "number" ? 0 : unreadRes?.mandatory_unread ?? 0;
        // Popup góc chỉ cho thông báo thường; bắt buộc do AdminMandatoryNotifications xử lý
        if (cancelled || mandatoryUnread > 0) return;
        if (!count || count < 1) return;

        const res = await getNotifications({
          page: 1,
          limit: PREVIEW_LIMIT,
          status: "unread",
        });
        if (cancelled) return;

        const unreadList = (res.items || []).slice(0, PREVIEW_LIMIT);

        setUnreadCount(count);
        setPreview(unreadList);
        setVisible(true);

        timerRef.current = setTimeout(() => {
          setVisible(false);
          markDone();
          timerRef.current = null;
        }, DISPLAY_MS);
      } catch (e) {
        console.error("DesktopUnreadReminder:", e);
      }
    }

    run();

    const onMandatoryDone = () => run();
    window.addEventListener("mandatory-queue-updated", onMandatoryDone);

    return () => {
      cancelled = true;
      window.removeEventListener("mandatory-queue-updated", onMandatoryDone);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const canOpenNotificationsPage = hasPermission("system.notifications", "R");

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="status"
          aria-live="polite"
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.98 }}
          transition={{ duration: 0.22 }}
          className="fixed bottom-16 right-6 z-[100] w-[min(100vw-2rem,22rem)] pointer-events-auto"
        >
          <div className="rounded-2xl border border-gray-100 bg-white shadow-2xl shadow-slate-200/80 overflow-hidden">
            <div className="flex items-start gap-3 px-4 py-3 bg-gradient-to-r from-red-700 to-red-600 text-white">
              <div className="mt-0.5 p-2 rounded-xl bg-white/15">
                <FontAwesomeIcon icon={faBell} className="text-lg" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-black uppercase tracking-widest opacity-90">
                  Thông báo chưa đọc
                </p>
                <p className="text-sm font-bold leading-snug mt-0.5">
                  Bạn có {unreadCount > 99 ? "99+" : unreadCount} thông báo chưa đọc
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                className="p-1.5 rounded-lg hover:bg-white/15 transition-colors"
                aria-label="Đóng nhắc nhở"
              >
                <FontAwesomeIcon icon={faXmark} className="text-sm" />
              </button>
            </div>

            {preview.length > 0 && (
              <ul className="max-h-48 overflow-y-auto divide-y divide-gray-50 text-left">
                {preview.map((n) => (
                  <li key={n.id} className="px-4 py-2.5">
                    <p className="text-[13px] font-semibold text-gray-800 line-clamp-2 leading-snug">
                      {n.title || "Thông báo"}
                    </p>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wide mt-1">
                      {n.created_at ? dayjs(n.created_at).fromNow() : ""}
                    </p>
                  </li>
                ))}
              </ul>
            )}

            <div className="px-4 py-2.5 bg-gray-50/80 border-t border-gray-100 flex items-center justify-between gap-2">
              {canOpenNotificationsPage ? (
                <button
                  type="button"
                  onClick={() => {
                    close();
                    navigate("/system/notifications");
                  }}
                  className="text-[11px] font-black uppercase tracking-wider text-red-700 hover:text-red-800"
                >
                  Xem tất cả
                </button>
              ) : (
                <span className="text-[10px] text-gray-400 font-medium">Mở chuông trên header để xem</span>
              )}
              <span className="text-[10px] text-gray-400 tabular-nums">~5s</span>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
